/**
 * Validate and tidy a .bib file, reporting every change.
 *
 * Built on the lossless reader in `bibtex.ts`, so the output is the user's own file:
 * macros, `#` concatenations, comments and entries the reader could not parse all
 * survive. Every normalisation is an option and every one it applies is logged against
 * the entry and field it touched, so the report can be read line by line against the
 * original.
 *
 * Validation follows classic BibTeX — the required fields Oren Patashnik's *BibTeXing*
 * lists for the 14 standard entry types — while accepting the biblatex names for the
 * same data (`journaltitle`, `date`, `location`, `institution` for a thesis), because a
 * file written for biblatex is not wrong for using them.
 */

import {
  type BibDocument,
  type BibEntry,
  type BibField,
  type BibNode,
  type BibValuePart,
  collectMacros,
  fieldOf,
  fieldText,
  isOthers,
  latexToUnicode,
  MONTH_MACRO_NAMES,
  MONTH_NAMES,
  parseBibtex,
  parseBibtexName,
  serializeEntry,
  serializeNode,
  serializeValue,
  splitBibtexNames,
  valueText,
} from "@/lib/tool-kit/reference-tools/bibtex";

/* ---------------------------------------------------------------- options */

export type IndentStyle = "two" | "four" | "tab";
export type FieldOrder = "standard" | "keep" | "alphabetical";
export type SortOrder = "none" | "key" | "author" | "year-desc" | "year-asc";
export type CapsProtection = "none" | "acronyms" | "all";

export interface CleanOptions {
  /** `@ARTICLE` → `@article`, `Title` → `title`. */
  lowercase: boolean;
  fieldOrder: FieldOrder;
  indent: IndentStyle;
  /** `1-10` and `1–10` → `1--10` in `pages`. */
  pageDashes: boolean;
  /** `{March}`, `"3"`, `{Mar.}` → the bare macro `mar`. */
  monthMacros: boolean;
  removeEmpty: boolean;
  /** Brace-protect words in titles so styles that lower-case titles keep them. */
  protectCaps: CapsProtection;
  /** Collapse runs of spaces and line breaks inside values; trim their ends. */
  trimWhitespace: boolean;
  /** `"quoted"` values → `{braced}`. */
  braces: boolean;
  /** `https://doi.org/10.1/x` and `doi:10.1/x` in `doi` → `10.1/x`. */
  bareDoi: boolean;
  sort: SortOrder;
}

export const DEFAULT_CLEAN_OPTIONS: CleanOptions = {
  lowercase: true,
  fieldOrder: "standard",
  indent: "two",
  pageDashes: true,
  monthMacros: true,
  removeEmpty: true,
  protectCaps: "none",
  trimWhitespace: true,
  braces: true,
  bareDoi: true,
  sort: "none",
};

/* ------------------------------------------------------------- the report */

export type ChangeCode =
  | "type"
  | "fieldName"
  | "whitespace"
  | "pages"
  | "month"
  | "empty"
  | "caps"
  | "braces"
  | "doi"
  | "order";

export interface Change {
  /** The entry's key, or `@string name` for a macro definition. */
  key: string;
  line: number;
  code: ChangeCode;
  field?: string;
  before?: string;
  after?: string;
}

export type IssueCode =
  | "parse"
  | "missingKey"
  | "invalidKey"
  | "duplicateKey"
  | "duplicateKeyCase"
  | "missingField"
  | "biblatexType"
  | "unknownType"
  | "duplicateField"
  | "undefinedMacro"
  | "sameDoi"
  | "sameTitle"
  | "badYear";

export interface Issue {
  severity: "error" | "warning";
  code: IssueCode;
  /** The entry's key; null for a problem outside an entry. */
  key: string | null;
  line: number;
  /** The field, macro, type or parser error the issue is about. */
  detail?: string;
  /** The other entry's key, for duplicates. */
  other?: string;
}

export interface CleanResult {
  output: string;
  entries: number;
  strings: number;
  changes: Change[];
  issues: Issue[];
  sorted: boolean;
}

/* ------------------------------------------------------------- the rules */

/** Each inner list is a set of alternatives: `["author", "editor"]` needs one of them. */
const REQUIRED: Record<string, readonly (readonly string[])[]> = {
  article: [["author"], ["title"], ["journal"], ["year"]],
  book: [["author", "editor"], ["title"], ["publisher"], ["year"]],
  booklet: [["title"]],
  conference: [["author"], ["title"], ["booktitle"], ["year"]],
  inbook: [
    ["author", "editor"],
    ["title"],
    ["chapter", "pages"],
    ["publisher"],
    ["year"],
  ],
  incollection: [["author"], ["title"], ["booktitle"], ["publisher"], ["year"]],
  inproceedings: [["author"], ["title"], ["booktitle"], ["year"]],
  manual: [["title"]],
  mastersthesis: [["author"], ["title"], ["school"], ["year"]],
  misc: [],
  phdthesis: [["author"], ["title"], ["school"], ["year"]],
  proceedings: [["title"], ["year"]],
  techreport: [["author"], ["title"], ["institution"], ["year"]],
  unpublished: [["author"], ["title"], ["note"]],
};

/** biblatex's names for the same data. */
const ALIASES: Record<string, readonly string[]> = {
  journal: ["journaltitle"],
  year: ["date"],
  school: ["institution"],
  address: ["location"],
};

/** Entry types biblatex defines that classic BibTeX styles do not. */
const BIBLATEX_TYPES = new Set([
  "artwork",
  "audio",
  "bibnote",
  "bookinbook",
  "collection",
  "commentary",
  "dataset",
  "electronic",
  "image",
  "inreference",
  "jurisdiction",
  "legal",
  "legislation",
  "letter",
  "movie",
  "music",
  "mvbook",
  "mvcollection",
  "mvproceedings",
  "mvreference",
  "online",
  "patent",
  "performance",
  "periodical",
  "reference",
  "report",
  "review",
  "set",
  "software",
  "standard",
  "suppbook",
  "suppcollection",
  "suppperiodical",
  "thesis",
  "video",
  "www",
  "xdata",
]);

/** The field order "standard" writes; anything else follows in its original order. */
const STANDARD_ORDER = [
  "author",
  "editor",
  "translator",
  "title",
  "subtitle",
  "shorttitle",
  "booktitle",
  "journal",
  "journaltitle",
  "series",
  "edition",
  "volume",
  "number",
  "issue",
  "chapter",
  "pages",
  "eid",
  "year",
  "month",
  "day",
  "date",
  "publisher",
  "organization",
  "institution",
  "school",
  "address",
  "location",
  "howpublished",
  "type",
  "note",
  "isbn",
  "issn",
  "doi",
  "url",
  "urldate",
  "eprint",
  "eprinttype",
  "eprintclass",
  "archiveprefix",
  "primaryclass",
  "pmid",
  "language",
  "abstract",
  "keywords",
  "file",
];
const ORDER_RANK = new Map(STANDARD_ORDER.map((name, index) => [name, index]));

const TITLE_FIELDS = new Set([
  "title",
  "booktitle",
  "subtitle",
  "maintitle",
  "shorttitle",
]);

const MONTH_LOOKUP = new Map<string, string>();
for (const [index, macro] of MONTH_MACRO_NAMES.entries()) {
  const full = MONTH_NAMES[index].toLowerCase();
  MONTH_LOOKUP.set(macro, macro);
  MONTH_LOOKUP.set(full, macro);
  MONTH_LOOKUP.set(String(index + 1), macro);
  MONTH_LOOKUP.set(String(index + 1).padStart(2, "0"), macro);
}
MONTH_LOOKUP.set("sept", "sep");

const INDENTS: Record<IndentStyle, string> = {
  two: "  ",
  four: "    ",
  tab: "\t",
};

const RE_SPACE_RUN = /\s+/g;
const RE_LEADING_SPACE = /^\s+/;
const RE_TRAILING_SPACE = /\s+$/;
const RE_TRAILING_DOT = /\.$/;
const RE_PAGE_DASH =
  /(?<=[\p{L}\p{N}])\s*(?:-|–|—|−|‐|‑)(?!-)\s*(?=[\p{L}\p{N}])/gu;
const RE_INVALID_KEY = /[\s"#%'(),={}]/;
const RE_FOUR_DIGITS = /^\d{4}$/;
const RE_DOI_PREFIX = /^(?:https?:\/\/(?:dx\.)?doi\.org\/|doi:\s*)/i;
const RE_DIACRITICS = /\p{M}/gu;
const RE_NOT_ALNUM = /[^\p{L}\p{N}]+/gu;
const RE_WORD_SPLIT = /(\s+)/;
const RE_WORD_CORE = /^([^\p{L}\p{N}]*)(.*?)([^\p{L}\p{N}]*)$/u;
const RE_INNER_CAPITAL = /^.+\p{Lu}/u;
const RE_LEADING_CAPITAL = /^\p{Lu}/u;
const RE_LATEX_OR_BRACE = /[\\${}]/;
const RE_LETTER = /\p{L}/u;

/* ---------------------------------------------------------- small helpers */

function lower(name: string): string {
  return name.toLowerCase();
}

function textOf(parts: readonly BibValuePart[]): string {
  return parts.map((part) => part.text).join("");
}

function isTextPart(part: BibValuePart): boolean {
  return part.kind === "braced" || part.kind === "quoted";
}

function braceDepthChange(text: string): number {
  let depth = 0;
  for (const char of text) {
    if (char === "{") {
      depth += 1;
    } else if (char === "}") {
      depth -= 1;
    }
  }
  return depth;
}

function balanced(text: string): boolean {
  let depth = 0;
  for (const char of text) {
    if (char === "{") {
      depth += 1;
    } else if (char === "}") {
      depth -= 1;
      if (depth < 0) {
        return false;
      }
    }
  }
  return depth === 0;
}

/** A value with nothing in it once macros are expanded. */
function isEmptyValue(
  parts: readonly BibValuePart[],
  macros: ReadonlyMap<string, string>
): boolean {
  return valueText(parts, macros).trim() === "";
}

function hasField(entry: BibEntry, name: string): boolean {
  const names = [name, ...(ALIASES[name] ?? [])];
  return names.some((candidate) => {
    const field = fieldOf(entry, candidate);
    return field !== undefined && textOf(field.parts).trim() !== "";
  });
}

export function normalizeDoi(raw: string): string {
  return raw.trim().replace(RE_DOI_PREFIX, "").toLowerCase();
}

/** Lower-case letters and digits only, diacritics folded: for comparing titles. */
export function foldTitle(raw: string): string {
  return latexToUnicode(raw)
    .normalize("NFKD")
    .replace(RE_DIACRITICS, "")
    .toLowerCase()
    .replace(RE_NOT_ALNUM, " ")
    .trim();
}

/* ------------------------------------------------------------- validation */

function entryLabel(entry: BibEntry): string | null {
  return entry.key === "" ? null : entry.key;
}

function checkKey(
  entry: BibEntry,
  seen: Map<string, BibEntry>,
  issues: Issue[]
): void {
  const key = entry.key;
  if (key === "") {
    issues.push({
      severity: "error",
      code: "missingKey",
      key: null,
      line: entry.line,
      detail: entry.type,
    });
    return;
  }
  if (RE_INVALID_KEY.test(key)) {
    issues.push({
      severity: "error",
      code: "invalidKey",
      key,
      line: entry.line,
    });
  }
  const earlier = seen.get(lower(key));
  if (!earlier) {
    seen.set(lower(key), entry);
    return;
  }
  issues.push({
    severity: earlier.key === key ? "error" : "warning",
    code: earlier.key === key ? "duplicateKey" : "duplicateKeyCase",
    key,
    line: entry.line,
    other: earlier.key,
  });
}

function checkType(entry: BibEntry, issues: Issue[]): void {
  const type = lower(entry.type);
  const required = REQUIRED[type];
  if (!required) {
    issues.push({
      severity: "warning",
      code: BIBLATEX_TYPES.has(type) ? "biblatexType" : "unknownType",
      key: entryLabel(entry),
      line: entry.line,
      detail: type,
    });
    return;
  }
  for (const alternatives of required) {
    if (!alternatives.some((name) => hasField(entry, name))) {
      issues.push({
        severity: "error",
        code: "missingField",
        key: entryLabel(entry),
        line: entry.line,
        detail: alternatives.join(" / "),
      });
    }
  }
}

function checkFields(
  entry: BibEntry,
  macros: ReadonlyMap<string, string>,
  issues: Issue[]
): void {
  const counted = new Set<string>();
  for (const field of entry.fields) {
    const name = lower(field.name);
    if (counted.has(name)) {
      issues.push({
        severity: "warning",
        code: "duplicateField",
        key: entryLabel(entry),
        line: field.line,
        detail: name,
      });
    }
    counted.add(name);
    for (const part of field.parts) {
      if (part.kind === "macro" && !macros.has(lower(part.text))) {
        issues.push({
          severity: "error",
          code: "undefinedMacro",
          key: entryLabel(entry),
          line: field.line,
          detail: part.text,
        });
      }
    }
  }
  const year = fieldText(entry, "year", macros);
  if (year !== "" && !RE_FOUR_DIGITS.test(year)) {
    issues.push({
      severity: "warning",
      code: "badYear",
      key: entryLabel(entry),
      line: fieldOf(entry, "year")?.line ?? entry.line,
      detail: year,
    });
  }
}

interface DuplicateIndex {
  dois: Map<string, BibEntry>;
  titles: Map<string, BibEntry>;
}

function checkDuplicate(
  entry: BibEntry,
  macros: ReadonlyMap<string, string>,
  index: DuplicateIndex,
  issues: Issue[]
): void {
  const doi = normalizeDoi(fieldText(entry, "doi", macros));
  if (doi !== "") {
    const earlier = index.dois.get(doi);
    if (earlier && earlier !== entry) {
      issues.push({
        severity: "warning",
        code: "sameDoi",
        key: entryLabel(entry),
        line: entry.line,
        detail: doi,
        other: earlier.key,
      });
      return;
    }
    index.dois.set(doi, entry);
  }
  const title = foldTitle(fieldText(entry, "title", macros));
  if (title.split(" ").length < 3) {
    return;
  }
  const year = fieldText(entry, "year", macros);
  const earlier = index.titles.get(title);
  if (!earlier) {
    index.titles.set(title, entry);
    return;
  }
  const earlierYear = fieldText(earlier, "year", macros);
  if (year === "" || earlierYear === "" || year === earlierYear) {
    issues.push({
      severity: "warning",
      code: "sameTitle",
      key: entryLabel(entry),
      line: entry.line,
      other: earlier.key,
    });
  }
}

/** Every problem in a parsed file, in file order. */
export function validateBibtex(doc: BibDocument): Issue[] {
  // A missing key is reported once, by checkKey, with the entry it belongs to.
  const issues: Issue[] = doc.errors
    .filter((error) => error.code !== "missingKey")
    .map((error) => ({
      severity: "error" as const,
      code: "parse" as const,
      key: error.context ?? null,
      line: error.line,
      detail: error.code,
    }));
  const macros = collectMacros(doc.nodes);
  const keys = new Map<string, BibEntry>();
  const duplicates: DuplicateIndex = { dois: new Map(), titles: new Map() };
  for (const node of doc.nodes) {
    if (node.kind !== "entry") {
      continue;
    }
    checkKey(node, keys, issues);
    checkType(node, issues);
    checkFields(node, macros, issues);
    checkDuplicate(node, macros, duplicates, issues);
  }
  return issues.sort((a, b) => a.line - b.line);
}

/* ---------------------------------------------------------- normalisation */

function trimParts(parts: readonly BibValuePart[]): BibValuePart[] {
  const out = parts.map((part) =>
    isTextPart(part)
      ? { ...part, text: part.text.replace(RE_SPACE_RUN, " ") }
      : part
  );
  const first = out[0];
  if (first && isTextPart(first)) {
    out[0] = { ...first, text: first.text.replace(RE_LEADING_SPACE, "") };
  }
  const lastIndex = out.length - 1;
  const last = out[lastIndex];
  if (last && isTextPart(last)) {
    out[lastIndex] = {
      ...last,
      text: last.text.replace(RE_TRAILING_SPACE, ""),
    };
  }
  return out;
}

function dashPages(parts: readonly BibValuePart[]): BibValuePart[] {
  return parts.map((part) =>
    isTextPart(part)
      ? { ...part, text: part.text.replace(RE_PAGE_DASH, "--") }
      : part
  );
}

/** `{March}`, `{Mar.}`, `"3"` → `mar`. A value with a day in it is left alone. */
function monthMacro(
  parts: readonly BibValuePart[],
  macros: ReadonlyMap<string, string>
): BibValuePart[] {
  const spoken = valueText(parts, macros)
    .trim()
    .toLowerCase()
    .replace(RE_TRAILING_DOT, "");
  const macro = MONTH_LOOKUP.get(spoken);
  return macro ? [{ kind: "macro", text: macro }] : [...parts];
}

/** Strip a resolver prefix: styles that print `doi` add their own. */
function bareDoi(parts: readonly BibValuePart[]): BibValuePart[] {
  return parts.map((part) =>
    isTextPart(part)
      ? { ...part, text: part.text.replace(RE_DOI_PREFIX, "") }
      : part
  );
}

function bracesFor(parts: readonly BibValuePart[]): BibValuePart[] {
  return parts.map((part) =>
    part.kind === "quoted" && balanced(part.text)
      ? { kind: "braced", text: part.text }
      : part
  );
}

function shouldProtect(
  core: string,
  first: boolean,
  mode: CapsProtection
): boolean {
  if (!RE_LETTER.test(core)) {
    return false;
  }
  if (RE_INNER_CAPITAL.test(core)) {
    return true;
  }
  return mode === "all" && !first && RE_LEADING_CAPITAL.test(core);
}

/**
 * Wrap the words at brace depth zero that a style might lower-case. Words already in
 * braces, LaTeX commands and math are left alone; punctuation stays outside the braces.
 */
function protectText(
  text: string,
  mode: CapsProtection,
  state: { depth: number; words: number }
): string {
  const pieces = text.split(RE_WORD_SPLIT);
  return pieces
    .map((piece, index) => {
      if (index % 2 === 1 || piece === "") {
        return piece;
      }
      const startDepth = state.depth;
      state.depth += braceDepthChange(piece);
      const first = state.words === 0;
      state.words += 1;
      if (startDepth > 0 || RE_LATEX_OR_BRACE.test(piece)) {
        return piece;
      }
      const [, lead, core, trail] = RE_WORD_CORE.exec(piece) ?? [];
      if (core === undefined || !shouldProtect(core, first, mode)) {
        return piece;
      }
      return `${lead}{${core}}${trail}`;
    })
    .join("");
}

function protectCaps(
  parts: readonly BibValuePart[],
  mode: CapsProtection
): BibValuePart[] {
  const state = { depth: 0, words: 0 };
  return parts.map((part) =>
    isTextPart(part)
      ? { ...part, text: protectText(part.text, mode, state) }
      : part
  );
}

interface FieldContext {
  entryKey: string;
  options: CleanOptions;
  macros: ReadonlyMap<string, string>;
  changes: Change[];
}

function record(
  context: FieldContext,
  field: BibField,
  code: ChangeCode,
  before: BibValuePart[],
  after: BibValuePart[]
): void {
  const from = serializeValue(before);
  const to = serializeValue(after);
  if (from !== to) {
    context.changes.push({
      key: context.entryKey,
      line: field.line,
      code,
      field: lower(field.name),
      before: from,
      after: to,
    });
  }
}

/** Apply each value option in turn, logging the ones that change something. */
function cleanValue(field: BibField, context: FieldContext): BibValuePart[] {
  const { options, macros } = context;
  const name = lower(field.name);
  let parts = [...field.parts];
  const step = (
    enabled: boolean,
    code: ChangeCode,
    transform: (value: BibValuePart[]) => BibValuePart[]
  ) => {
    if (!enabled) {
      return;
    }
    const next = transform(parts);
    record(context, field, code, parts, next);
    parts = next;
  };
  step(options.trimWhitespace, "whitespace", trimParts);
  step(options.braces, "braces", bracesFor);
  step(options.pageDashes && name === "pages", "pages", dashPages);
  step(options.bareDoi && name === "doi", "doi", bareDoi);
  step(options.monthMacros && name === "month", "month", (value) =>
    monthMacro(value, macros)
  );
  step(
    options.protectCaps !== "none" && TITLE_FIELDS.has(name),
    "caps",
    (value) => protectCaps(value, options.protectCaps)
  );
  return parts;
}

function orderFields(fields: BibField[], order: FieldOrder): BibField[] {
  if (order === "keep") {
    return fields;
  }
  const indexed = fields.map((field, index) => ({ field, index }));
  const rankOf = (name: string) =>
    ORDER_RANK.get(lower(name)) ?? STANDARD_ORDER.length;
  indexed.sort((a, b) => {
    if (order === "alphabetical") {
      return (
        lower(a.field.name).localeCompare(lower(b.field.name)) ||
        a.index - b.index
      );
    }
    return rankOf(a.field.name) - rankOf(b.field.name) || a.index - b.index;
  });
  return indexed.map((item) => item.field);
}

function cleanEntry(
  entry: BibEntry,
  options: CleanOptions,
  macros: ReadonlyMap<string, string>,
  changes: Change[]
): BibEntry {
  const key = entry.key || `@${entry.type}`;
  const type = options.lowercase ? lower(entry.type) : entry.type;
  if (type !== entry.type) {
    changes.push({
      key,
      line: entry.line,
      code: "type",
      before: `@${entry.type}`,
      after: `@${type}`,
    });
  }
  const context: FieldContext = { entryKey: key, options, macros, changes };
  const fields: BibField[] = [];
  for (const field of entry.fields) {
    const name = options.lowercase ? lower(field.name) : field.name;
    if (name !== field.name) {
      changes.push({
        key,
        line: field.line,
        code: "fieldName",
        field: lower(field.name),
        before: field.name,
        after: name,
      });
    }
    if (options.removeEmpty && isEmptyValue(field.parts, macros)) {
      changes.push({
        key,
        line: field.line,
        code: "empty",
        field: lower(field.name),
        before: serializeValue(field.parts),
      });
      continue;
    }
    fields.push({ ...field, name, parts: cleanValue(field, context) });
  }
  const ordered = orderFields(fields, options.fieldOrder);
  if (ordered.some((field, index) => field !== fields[index])) {
    changes.push({ key, line: entry.line, code: "order" });
  }
  return { ...entry, type, fields: ordered };
}

function cleanNode(
  node: BibNode,
  options: CleanOptions,
  macros: ReadonlyMap<string, string>,
  changes: Change[]
): BibNode {
  if (node.kind === "entry") {
    return cleanEntry(node, options, macros, changes);
  }
  if (node.kind === "string" || node.kind === "preamble") {
    const type = options.lowercase ? lower(node.type) : node.type;
    const label = node.kind === "string" ? `@string ${node.name}` : "@preamble";
    if (type !== node.type) {
      changes.push({
        key: label,
        line: node.line,
        code: "type",
        before: `@${node.type}`,
        after: `@${type}`,
      });
    }
    if (node.kind === "preamble") {
      return { ...node, type };
    }
    const asField: BibField = {
      name: node.name,
      parts: node.parts,
      line: node.line,
    };
    const context: FieldContext = { entryKey: label, options, macros, changes };
    return { ...node, type, parts: cleanValue(asField, context) };
  }
  return node;
}

/* ---------------------------------------------------------------- sorting */

interface SortKeys {
  key: string;
  author: string;
  year: number | null;
  title: string;
}

function sortKeys(
  entry: BibEntry,
  macros: ReadonlyMap<string, string>
): SortKeys {
  const people =
    fieldText(entry, "author", macros) || fieldText(entry, "editor", macros);
  const firstName = splitBibtexNames(people).find((name) => !isOthers(name));
  const parsed = firstName ? parseBibtexName(firstName) : null;
  // No author: file it under its title, as BibTeX's own styles do.
  const author = parsed
    ? `${parsed.last} ${parsed.first}`
    : fieldText(entry, "title", macros) || entry.key;
  const yearText =
    fieldText(entry, "year", macros) || fieldText(entry, "date", macros);
  const year = Number.parseInt(yearText.slice(0, 4), 10);
  return {
    key: entry.key,
    author: foldTitle(author),
    year: Number.isFinite(year) ? year : null,
    title: foldTitle(fieldText(entry, "title", macros)),
  };
}

const COLLATOR = new Intl.Collator("en", {
  numeric: true,
  sensitivity: "base",
});

function compareYears(a: number | null, b: number | null, newest: boolean) {
  if (a === b) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  return newest ? b - a : a - b;
}

function compareEntries(a: SortKeys, b: SortKeys, order: SortOrder): number {
  const byAuthor =
    COLLATOR.compare(a.author, b.author) ||
    compareYears(a.year, b.year, false) ||
    COLLATOR.compare(a.title, b.title);
  if (order === "key") {
    return COLLATOR.compare(a.key, b.key);
  }
  if (order === "author") {
    return byAuthor;
  }
  return compareYears(a.year, b.year, order === "year-desc") || byAuthor;
}

/**
 * Entries sorted; `@preamble`, `@string` and comments first, in their own order,
 * because a macro must be defined before an entry uses it. Unreadable entries go last,
 * verbatim, so nothing is lost.
 */
function sortNodes(
  nodes: BibNode[],
  order: SortOrder,
  macros: ReadonlyMap<string, string>
): BibNode[] {
  const entries = nodes.filter(
    (node): node is BibEntry => node.kind === "entry"
  );
  const keyed = entries.map((entry) => ({
    entry,
    keys: sortKeys(entry, macros),
  }));
  keyed.sort((a, b) => compareEntries(a.keys, b.keys, order));
  return [
    ...nodes.filter((node) => node.kind !== "entry" && node.kind !== "broken"),
    ...keyed.map((item) => item.entry),
    ...nodes.filter((node) => node.kind === "broken"),
  ];
}

/* ------------------------------------------------------------------ clean */

export function cleanBibtex(text: string, options: CleanOptions): CleanResult {
  const doc = parseBibtex(text);
  const issues = validateBibtex(doc);
  const macros = collectMacros(doc.nodes);
  const changes: Change[] = [];
  const cleaned = doc.nodes.map((node) =>
    cleanNode(node, options, macros, changes)
  );
  const sorted = options.sort !== "none";
  const nodes = sorted ? sortNodes(cleaned, options.sort, macros) : cleaned;
  const indent = INDENTS[options.indent];
  const output = nodes
    .map((node) =>
      node.kind === "entry"
        ? serializeEntry(node, { indent })
        : serializeNode(node, { indent })
    )
    .filter((block) => block.trim() !== "")
    .join("\n\n");
  return {
    output: output === "" ? "" : `${output}\n`,
    entries: doc.nodes.filter((node) => node.kind === "entry").length,
    strings: doc.nodes.filter((node) => node.kind === "string").length,
    changes,
    issues,
    sorted,
  };
}
